import { PublicClientApplication, BrowserAuthError } from "@azure/msal-browser";
import { msalConfig, getMsalScopes } from "../utils/msalConfig";

class AuthService {
    constructor() {
        this.msalInstance = new PublicClientApplication(msalConfig);
        this.isInitialized = false;
    }

    async initialize() {
        if (this.isInitialized) return;

        if (!this.initPromise) {
            this.initPromise = (async () => {
                try {
                    await this.msalInstance.initialize();

                    // Set active account from cache if available
                    const accounts = this.msalInstance.getAllAccounts();
                    if (accounts.length > 0 && !this.msalInstance.getActiveAccount()) {
                        this.msalInstance.setActiveAccount(accounts[0]);
                    }

                    this.isInitialized = true;

                    // Start auto-refresh if we have an account
                    if (this.getAccount()) {
                        this.startTokenRefresh();
                    }
                } catch (e) {
                    console.error("MSAL Initialization Failed", e);
                    throw e;
                } finally {
                    this.initPromise = null;
                }
            })();
        }

        await this.initPromise;
    }

    async login() {
        await this.initialize();

        const loginRequest = {
            scopes: getMsalScopes(),
        };

        try {
            const response = await this.msalInstance.loginPopup(loginRequest);
            this.handleResponse(response);
            this.startTokenRefresh(); // Start refreshing after login
            return response;
        } catch (error) {
            // Check for interaction_in_progress and handle gracefully
            if (error instanceof BrowserAuthError && error.errorCode === "interaction_in_progress") {
                console.warn("Interaction is currently in progress. Please close any open popups and try again.");
                // Optionally return null or undefined to signal no new login occurred
                return null;
            }
            console.error("Login failed", error);
            throw error;
        }
    }

    async logout() {
        this.stopTokenRefresh(); // Stop refreshing before logout
        await this.initialize();
        const logoutRequest = {
            account: this.msalInstance.getActiveAccount(),
            postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri
        };
        localStorage.removeItem('msalAccount');
        await this.msalInstance.logoutPopup(logoutRequest);
    }

    handleResponse(response) {
        if (response !== null) {
            this.msalInstance.setActiveAccount(response.account);
            localStorage.setItem('msalAccount', JSON.stringify(response));
            console.log("Logged in options: ", response);
        }
    }

    getAccount() {
        return this.msalInstance.getActiveAccount();
    }

    async getAccessToken() {
        await this.initialize();
        const account = this.getAccount();
        if (!account) return null;

        // Deduplication: Return existing promise if request is in flight
        if (this.tokenRequestPromise) {
            return this.tokenRequestPromise;
        }

        const request = {
            scopes: getMsalScopes(),
            account: account
        };

        this.tokenRequestPromise = (async () => {
            try {
                const response = await this.msalInstance.acquireTokenSilent(request);
                return response.accessToken;
            } catch (error) {
                console.warn("Silent token acquisition failed", error);
                // Fallback handled by caller or interceptor if needed
                return null;
            } finally {
                this.tokenRequestPromise = null;
            }
        })();

        return this.tokenRequestPromise;
    }

    startTokenRefresh() {
        this.stopTokenRefresh(); // Ensure no duplicate intervals
        // Refresh every 5 seconds (5000ms)
        this.refreshInterval = setInterval(async () => {
            await this.getAccessToken();
        }, 5000);
    }

    stopTokenRefresh() {
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
            this.refreshInterval = null;
        }
    }
}

export default new AuthService();
